import { ThemeEvents, VariantUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { AddToCartComponent } from '@theme/product-form';
import { isMobileBreakpoint, normalizeOptionValueKey } from '@theme/utilities';

/** @type {WeakMap<Element, AbortController>} */
const stackCardSectionFetch = new WeakMap();

/** @type {WeakSet<Element>} */
const stackCartFailHandling = new WeakSet();

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeOptionValueKeyForMatch(value) {
  return normalizeOptionValueKey(value).replace(/["'\\]/g, '');
}

/**
 * @param {OptionsBlockVariant[]} variants
 * @returns {boolean}
 */
function stackVariantsHaveMultipleTier0Values(variants) {
  const seen = new Set();
  for (const v of variants) {
    if (!v?.options?.length) continue;
    ensureVariantOptionsNormalized(v);
    const key = normalizeOptionValueKey(v.options[0]);
    if (!key) continue;
    seen.add(key);
    if (seen.size > 1) return true;
  }
  return false;
}

/**
 * @param {HTMLFormElement} form
 */
function clearStackLowerTierCartFailedFlags(form) {
  for (const input of form.querySelectorAll(
    '.options-block__stack-lower-tiers input[type="radio"][data-input-id][data-stack-cart-failed="true"]'
  )) {
    if (!(input instanceof HTMLInputElement)) continue;
    input.disabled = false;
    input.removeAttribute('aria-disabled');
    input.removeAttribute('data-stack-cart-failed');
    input.setAttribute('data-option-available', 'true');
    syncStackSwatchStrikethrough(input, true);
  }
  for (const sel of form.querySelectorAll('.options-block__stack-lower-tiers select.variant-option__select')) {
    if (!(sel instanceof HTMLSelectElement)) continue;
    for (const option of sel.options) {
      if (option.getAttribute('data-stack-cart-failed') !== 'true') continue;
      option.disabled = false;
      option.removeAttribute('data-stack-cart-failed');
      option.setAttribute('data-option-available', 'true');
    }
  }
}

/**
 * @typedef {object} OptionsBlockVariant
 * @property {number} [id]
 * @property {string[]|undefined} [options]
 * @property {string} [option1]
 * @property {string} [option2]
 * @property {string} [option3]
 * @property {boolean} [available]
 * @property {string} [image]
 * @property {object} [featured_image]
 * @property {string} [featured_image.src]
 * @property {string} [featured_image.url]
 * @property {object} [featured_media]
 * @property {object} [featured_media.preview_image]
 * @property {string} [featured_media.preview_image.src]
 * @property {string} [url]
 */

/**
 * @param {OptionsBlockVariant} v
 * @param {string} pickerProductUrl
 * @param {Element | null | undefined} fromEl
 * @returns {string}
 */
function sectionRenderingProductPathname(v, pickerProductUrl, fromEl) {
  const fallback = (pickerProductUrl.split('?')[0] || '').trim();
  if (typeof v.url === 'string' && v.url.trim()) {
    try {
      return new URL(v.url, window.location.origin).pathname;
    } catch {
      // fall through
    }
  }
  if (fromEl instanceof HTMLInputElement || fromEl instanceof HTMLOptionElement) {
    const connected = (fromEl.dataset.connectedProductUrl || '').trim();
    if (connected) {
      try {
        return new URL(connected, window.location.origin).pathname;
      } catch {
        // fall through
      }
    }
  }
  return fallback;
}

/**
 * @param {HTMLElement} picker
 * @param {Element | null | undefined} card
 * @param {Element | null | undefined} fromEl
 * @returns {string}
 */
function stackVariantUpdateSourceId(picker, card, fromEl) {
  try {
    const id = /** @type {{ selectedOptionId?: string }} */ (picker).selectedOptionId;
    if (typeof id === 'string' && id !== '') return id;
  } catch {
    console.error('Error getting selected option ID from picker', picker);
  }
  if (fromEl instanceof HTMLInputElement && fromEl.dataset.optionValueId) {
    return fromEl.dataset.optionValueId;
  }
  if (fromEl instanceof HTMLOptionElement && fromEl.dataset.optionValueId) {
    return fromEl.dataset.optionValueId;
  }
  if (card instanceof Element) {
    const swatchChecked = card.querySelector(
      'fieldset.variant-option--swatches input[type="radio"]:checked[data-option-value-id]'
    );
    if (swatchChecked instanceof HTMLInputElement && swatchChecked.dataset.optionValueId) {
      return swatchChecked.dataset.optionValueId;
    }
  }
  const checkedInPicker = picker.querySelector(
    'fieldset input[type="radio"]:checked[data-option-value-id]'
  );
  if (checkedInPicker instanceof HTMLInputElement && checkedInPicker.dataset.optionValueId) {
    return checkedInPicker.dataset.optionValueId;
  }
  return '';
}

/**
 * @param {string | null | undefined} s
 * @returns {string}
 */
function optStr(s) {
  if (s == null) return '';
  return String(s).trim();
}

/**
 * @param {string | null | undefined} a
 * @param {string | null | undefined} b
 * @returns {boolean}
 */
function sameOptionValue(a, b) {
  return normalizeOptionValueKey(a) === normalizeOptionValueKey(b);
}

/**
 * @param {string | null | undefined} s
 * @returns {boolean}
 */
function slotHasValue(s) {
  return optStr(s) !== '';
}

/**
 * Prefer option1–3 (Shopify option order) over `options` when both exist — they can disagree.
 * @param {OptionsBlockVariant} v
 * @returns {void}
 */
function ensureVariantOptionsNormalized(v) {
  if (!v || typeof v !== 'object') return;
  const o = [];
  for (const k of /** @type {const} */ (['option1', 'option2', 'option3'])) {
    if (v[k] == null || v[k] === '') break;
    o.push(optStr(v[k]));
  }
  if (o.length) {
    v.options = o;
    return;
  }
  if (Array.isArray(v.options) && v.options.length) {
    v.options = v.options.map((x) => optStr(x));
  }
}

/**
 * @param {Element | null | undefined} script
 * @returns {number}
 */
function scriptVariantCount(script) {
  if (!script?.textContent) return 0;
  try {
    const parsed = JSON.parse(script.textContent);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

/**
 * @param {Element | null | undefined} card
 * @param {Element | null | undefined} stackRoot
 * @param {HTMLFormElement | null | undefined} form
 * @returns {boolean}
 */
function tryApplySingleVariantQuickAdd(card, stackRoot, form) {
  const quick = card?.querySelector('quick-add-component');
  if (!quick || optionCountFromQuick(quick) >= 2) return false;

  let script = form?.querySelector('script[data-options-block-variants]') ?? null;
  if (!script?.textContent && stackRoot) {
    script = stackRoot.querySelector('script[data-options-block-variants]');
  }
  if (!script?.textContent) return false;

  try {
    const variants = JSON.parse(script.textContent);
    if (!Array.isArray(variants) || variants.length !== 1) return false;
    const v = variants[0];
    if (v == null || v.id == null) return false;
    ensureVariantOptionsNormalized(v);
    const idInput = quick.querySelector('form input[name="id"]');
    if (idInput instanceof HTMLInputElement) {
      idInput.value = String(v.id);
      idInput.disabled = v.available === false;
    }
    quick.setAttribute('data-quick-add-button', 'add');
    const atc = quick.querySelector('add-to-cart-component');
    if (atc instanceof AddToCartComponent) {
      if (v.available) {
        atc.enable();
      } else {
        atc.disable();
      }
    }
    syncAddToCartFlyImage(quick, v);
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {Element | null | undefined} script
 * @returns {number}
 */
function optionCountFromScript(script) {
  if (!script?.textContent) return 0;
  try {
    const parsed = JSON.parse(script.textContent);
    const first = Array.isArray(parsed) ? parsed[0] : null;
    if (!first || typeof first !== 'object') return 0;
    let n = 0;
    for (const k of ['option1', 'option2', 'option3']) {
      if (first[k] == null || first[k] === '') break;
      n++;
    }
    if (n) return n;
    if (Array.isArray(first.options) && first.options.length) return first.options.length;
    return 0;
  } catch {
    return 0;
  }
}

/**
 * @param {ParentNode} scope
 * @param {number} tierIndex
 * @returns {HTMLInputElement | HTMLSelectElement | null}
 */
function findCheckedControlOnTier(scope, tierIndex) {
  if (!(scope instanceof Element)) return null;
  for (const el of scope.querySelectorAll('input[type="radio"]:checked')) {
    if (!(el instanceof HTMLInputElement)) continue;
    if (el.closest('.options-block__main-picker--hide-swatch-fieldsets')) continue;
    if (el.closest('[data-erp-masked-option="true"]')) continue;
    if (optionIndexFromControl(el) === tierIndex) return el;
  }
  for (const el of scope.querySelectorAll('select.variant-option__select')) {
    if (!(el instanceof HTMLSelectElement)) continue;
    if (el.closest('.options-block__main-picker--hide-swatch-fieldsets')) continue;
    if (el.closest('[data-erp-masked-option="true"]')) continue;
    if (optionIndexFromControl(el) === tierIndex) {
      const opt = el.options[el.selectedIndex];
      if (opt?.value) return el;
    }
  }
  return null;
}

/**
 * @param {Element | null | undefined} card
 * @param {Element | null | undefined} start
 * @param {Element | null | undefined} fromEl
 * @returns {HTMLElement | null}
 */
function resolveStackRoot(card, start, fromEl) {
  if (fromEl instanceof Element) {
    const st = fromEl.closest('.options-block--opt-stack');
    if (st instanceof HTMLElement) {
      if (!card || card.contains(st)) return st;
    }
  }
  if (start instanceof HTMLElement && start.classList?.contains('options-block--opt-stack')) {
    return start;
  }
  if (start instanceof Element) {
    const st0 = start.closest('.options-block--opt-stack');
    if (st0 instanceof HTMLElement) return st0;
  }
  if (card) {
    const first = card.querySelector('.options-block--opt-stack');
    if (first instanceof HTMLElement) return first;
  }
  return null;
}

/**
 * @param {Element | null | undefined} fromEl
 * @param {HTMLElement | null} stackRoot
 * @param {Element | null | undefined} card
 * @returns {HTMLFormElement | null}
 */
function resolveStackForm(fromEl, stackRoot, card) {
  if (fromEl instanceof Element) {
    const f = fromEl.closest('form.options-block--stack-form');
    if (f instanceof HTMLFormElement) {
      if (stackRoot?.contains(f)) return f;
    }
  }
  const a = stackRoot?.querySelector('form.options-block--stack-form');
  if (a instanceof HTMLFormElement) return a;
  const b = card?.querySelector('form.options-block--stack-form');
  return b instanceof HTMLFormElement ? b : null;
}

/**
 * @param {string} src
 * @returns {string | null}
 */
function withWidth100(src) {
  if (!src || typeof src !== 'string') return null;
  if (src.includes('width=100') || src.includes('width%3D100')) return src;
  return src + (src.includes('?') ? '&' : '?') + 'width=100';
}

/**
 * @param {OptionsBlockVariant} v
 * @returns {string | null}
 */
function variantImageUrlForFlyFromJson(v) {
  if (!v || typeof v !== 'object') return null;
  const p = v.featured_media?.preview_image?.src;
  if (p && typeof p === 'string') return withWidth100(p);
  if (v.featured_image && typeof v.featured_image === 'object') {
    if (typeof v.featured_image.src === 'string' && v.featured_image.src) {
      return withWidth100(v.featured_image.src);
    }
    if (typeof v.featured_image.url === 'string' && v.featured_image.url) {
      return withWidth100(v.featured_image.url);
    }
  }
  if (typeof v.image === 'string' && v.image) return withWidth100(v.image);
  return null;
}

/**
 * @param {Element | null | undefined} quick
 * @param {OptionsBlockVariant} v
 */
function syncAddToCartFlyImage(quick, v) {
  if (!quick) return;
  const atc = quick.querySelector('add-to-cart-component');
  if (!(atc instanceof AddToCartComponent)) return;
  const url = variantImageUrlForFlyFromJson(v);
  if (url) {
    atc.setAttribute('data-product-variant-media', url);
  }
}

/**
 * @param {HTMLInputElement} input
 * @returns {number}
 */
function slotIndexFromRadio(input) {
  const raw = input.dataset.inputId;
  if (!raw) return -1;
  const pos = parseInt(String(String(raw).split('-')[0] || '0'), 10) - 1;
  return Number.isNaN(pos) ? -1 : pos;
}

/**
 * @param {Element} radioScope
 * @param {Element | null | undefined} script
 * @param {Element | null | undefined} fromEl
 * @returns {Array<string|undefined>}
 */
function readSlots(radioScope, script, fromEl) {
  const n = optionCountFromScript(script);
  const slots = new Array(n);
  if (!(radioScope instanceof Element) || n < 1) return slots;

  for (const el of radioScope.querySelectorAll('input[type="radio"]:checked, select.variant-option__select')) {
    if (el instanceof HTMLInputElement) {
      if (el.closest('.options-block__main-picker--hide-swatch-fieldsets')) continue;
      if (el.closest('[data-erp-masked-option="true"]')) continue;
      const pos = slotIndexFromRadio(el);
      if (pos >= 0 && pos < n) {
        const t = optStr(el.value);
        if (t !== '') slots[pos] = t;
      }
    } else if (el instanceof HTMLSelectElement) {
      if (el.closest('.options-block__main-picker--hide-swatch-fieldsets')) continue;
      if (el.closest('[data-erp-masked-option="true"]')) continue;
      const opt = el.options[el.selectedIndex];
      if (!opt) continue;
      const raw2 = opt.getAttribute('data-input-id');
      if (!raw2) continue;
      const pos2 = parseInt(String(String(raw2).split('-')[0] || '0'), 10) - 1;
      if (pos2 >= 0 && pos2 < n) {
        const t2 = optStr(opt.value);
        if (t2 !== '') slots[pos2] = t2;
      }
    }
  }

  if (
    fromEl instanceof HTMLInputElement &&
    fromEl.type === 'radio' &&
    fromEl.dataset.inputId &&
    fromEl.checked &&
    radioScope.contains(fromEl)
  ) {
    const pos = slotIndexFromRadio(fromEl);
    if (pos >= 0 && pos < n) {
      const t = optStr(fromEl.value);
      if (t !== '') slots[pos] = t;
    }
  } else if (fromEl instanceof HTMLSelectElement && radioScope.contains(fromEl)) {
    const opt0 = fromEl.options[fromEl.selectedIndex];
    if (opt0) {
      const r0 = opt0.getAttribute('data-input-id');
      if (r0) {
        const p0 = parseInt(String(String(r0).split('-')[0] || '0'), 10) - 1;
        if (p0 >= 0 && p0 < n) {
          const t0 = optStr(opt0.value);
          if (t0 !== '') slots[p0] = t0;
        }
      }
    }
  }
  return slots;
}

/**
 * @param {Element} radioScope
 * @param {Element | null | undefined} script
 * @param {Element | null | undefined} fromEl
 * @param {Element} erpSyncScope
 * @returns {Array<string|undefined>}
 */
function readSlotsForMatching(radioScope, script, fromEl, erpSyncScope) {
  const slots = readSlots(radioScope, script, fromEl);
  syncErpMaskedOptions(erpSyncScope, slots);
  return slots;
}

/**
 * @param {Element} el
 * @returns {number | null}
 */
function optionIndexFromControl(el) {
  if (!(el instanceof Element)) return null;
  const withIndex = el.closest('[data-fieldset-index]');
  if (withIndex instanceof HTMLElement) {
    const a = withIndex.getAttribute('data-fieldset-index');
    if (a != null && a !== '') {
      const f = parseInt(a, 10);
      if (!Number.isNaN(f) && f >= 0) return f;
    }
  }
  if (el instanceof HTMLInputElement && el.type === 'radio' && el.dataset.inputId) {
    return parseInt(String(String(el.dataset.inputId).split('-')[0] || '0'), 10) - 1;
  }
  if (el instanceof HTMLSelectElement) {
    const o = el.querySelector('option[data-input-id]');
    const raw = o?.getAttribute('data-input-id');
    if (raw) {
      return parseInt(String(String(raw).split('-')[0] || '0'), 10) - 1;
    }
  }
  return null;
}

/**
 * @param {Element | null | undefined} scope
 */
function syncStackRadioCheckedVisuals(scope) {
  if (!(scope instanceof Element)) return;
  for (const input of scope.querySelectorAll(
    'form.options-block--stack-form input[type="radio"][data-input-id]'
  )) {
    if (!(input instanceof HTMLInputElement)) continue;
    input.dataset.currentChecked = input.checked ? 'true' : 'false';
  }
}

/**
 * @param {HTMLFormElement} form
 */
function clearStackDeepTierSelections(form) {
  for (const input of form.querySelectorAll(
    '.options-block__stack-lower-tiers input[type="radio"][data-input-id]'
  )) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (isStackCartFailedControl(input)) continue;
    input.checked = false;
    input.dataset.currentChecked = 'false';
  }
  for (const sel of form.querySelectorAll('.options-block__stack-lower-tiers select.variant-option__select')) {
    if (!(sel instanceof HTMLSelectElement)) continue;
    sel.selectedIndex = -1;
  }
  syncStackRadioCheckedVisuals(form);
}

/**
 * @param {Element | null | undefined} el
 * @returns {boolean}
 */
function isStackCartFailedControl(el) {
  return el instanceof Element && el.getAttribute('data-stack-cart-failed') === 'true';
}

/**
 * @param {HTMLFormElement | null | undefined} form
 * @returns {boolean}
 */
function stackFormHasCartFailedLowerTierOptions(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  return Boolean(form.querySelector('.options-block__stack-lower-tiers [data-stack-cart-failed="true"]'));
}

/**
 * @param {HTMLInputElement} input
 */
function disableStackOptionInput(input) {
  input.checked = false;
  input.dataset.currentChecked = 'false';
  input.disabled = true;
  input.setAttribute('data-option-available', 'false');
  input.setAttribute('aria-disabled', 'true');
  input.setAttribute('data-stack-cart-failed', 'true');
  syncStackSwatchStrikethrough(input, false);
}

/**
 * @param {Element} card
 * @param {string} variantId
 * @returns {number}
 */
function disableStackOptionsForVariant(card, variantId) {
  const stackRoot = card.querySelector('.options-block--opt-stack');
  const form = resolveStackForm(null, stackRoot instanceof HTMLElement ? stackRoot : null, card);
  if (!(form instanceof HTMLFormElement) || !variantId) {
    return 0;
  }

  const lowerTiers = form.querySelector('.options-block__stack-lower-tiers');
  if (!(lowerTiers instanceof Element)) {
    return 0;
  }

  const script = form.querySelector('script[data-options-block-variants]');
  if (!script?.textContent) {
    return 0;
  }

  let variants;
  try {
    variants = JSON.parse(script.textContent);
  } catch {
    return 0;
  }
  if (!Array.isArray(variants)) return 0;

  const v = variants.find((row) => row != null && String(row.id) === String(variantId));
  let disabled = 0;

  if (v) {
    v.available = false;
    script.textContent = JSON.stringify(variants);
    const selectedScript = form.querySelector(
      'script[type="application/json"]:not([data-options-block-variants])'
    );
    if (selectedScript?.textContent) {
      try {
        const selected = JSON.parse(selectedScript.textContent);
        if (selected != null && String(selected.id) === String(variantId)) {
          selected.available = false;
          selectedScript.textContent = JSON.stringify(selected);
        }
      } catch {
        // ignore
      }
    }
  }

  const multiColor = stackVariantsHaveMultipleTier0Values(variants);

  if (v?.options?.length && !multiColor) {
    ensureVariantOptionsNormalized(v);
    for (const input of lowerTiers.querySelectorAll('input[type="radio"][data-input-id]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (isStackCartFailedControl(input)) continue;
      const pos = slotIndexFromRadio(input);
      if (pos < 1 || pos >= v.options.length) continue;
      const wantKey = normalizeOptionValueKeyForMatch(v.options[pos]);
      const gotKey = normalizeOptionValueKeyForMatch(input.value);
      if (!wantKey || wantKey !== gotKey) {
        continue;
      }
      disableStackOptionInput(input);
      disabled++;
    }
    for (const sel of lowerTiers.querySelectorAll('select.variant-option__select')) {
      if (!(sel instanceof HTMLSelectElement)) continue;
      for (const option of sel.options) {
        if (isStackCartFailedControl(option)) continue;
        const raw = option.getAttribute('data-input-id');
        if (!raw) continue;
        const pos = parseInt(String(String(raw).split('-')[0] || '0'), 10) - 1;
        if (pos < 1 || pos >= v.options.length) continue;
        const wantKey = normalizeOptionValueKeyForMatch(v.options[pos]);
        if (normalizeOptionValueKeyForMatch(option.value) !== wantKey) continue;
        option.disabled = true;
        option.setAttribute('data-option-available', 'false');
        option.setAttribute('data-stack-cart-failed', 'true');
        if (option.selected) {
          option.selected = false;
          sel.selectedIndex = -1;
        }
        disabled++;
      }
    }
  }

  if (disabled === 0 && !multiColor) {
    for (const input of lowerTiers.querySelectorAll('input[type="radio"][data-input-id]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (isStackCartFailedControl(input)) continue;
      disableStackOptionInput(input);
      disabled++;
    }
    for (const sel of lowerTiers.querySelectorAll('select.variant-option__select')) {
      if (!(sel instanceof HTMLSelectElement)) continue;
      for (const option of sel.options) {
        if (isStackCartFailedControl(option)) continue;
        option.disabled = true;
        option.setAttribute('data-option-available', 'false');
        option.setAttribute('data-stack-cart-failed', 'true');
        if (option.selected) {
          option.selected = false;
          sel.selectedIndex = -1;
        }
        disabled++;
      }
    }
  }

  syncStackRadioCheckedVisuals(form);
  return disabled;
}

/**
 * @param {Element} card
 */
function resetStackAfterCartAdd(card) {
  if (!(card instanceof Element)) return;
  const stackRoot = card.querySelector('.options-block--opt-stack');
  if (!(stackRoot instanceof HTMLElement)) return;
  const form = resolveStackForm(null, stackRoot, card);
  if (!(form instanceof HTMLFormElement)) return;

  for (const fs of card.querySelectorAll('.options-block__stack-lower-tiers .options-block-stack--open')) {
    fs.classList.remove('options-block-stack--open');
  }
  for (const gate of stackRoot.querySelectorAll('.options-block__swatch-gate.options-block-stack--open')) {
    gate.classList.remove('options-block-stack--open');
  }

  clearStackDeepTierSelections(form);
  card.removeAttribute('data-stack-notify-id');
  if (card instanceof HTMLElement) {
    delete card.dataset.stackRadioArrowNav;
    delete card.dataset.stackRadioKeyboardConfirm;
    delete card.dataset.stackCartFailVariantId;
    delete card.dataset.stackLastSubmitVariantId;
  }
  runRoot(stackRoot, false);
}

/**
 * @param {Element} card
 * @param {string} [variantIdOverride]
 */
function handleStackCartAddFailure(card, variantIdOverride) {
  if (!(card instanceof Element)) return;
  if (stackCartFailHandling.has(card)) {
    return;
  }
  stackCartFailHandling.add(card);

  const stackRoot = card.querySelector('.options-block--opt-stack');
  if (!(stackRoot instanceof HTMLElement)) {
    stackCartFailHandling.delete(card);
    return;
  }
  const form = resolveStackForm(null, stackRoot, card);

  const idInput = card.querySelector('quick-add-component form input[name="id"]');
  const variantId =
    variantIdOverride ||
    (card instanceof HTMLElement ? card.dataset.stackLastSubmitVariantId : '') ||
    (idInput instanceof HTMLInputElement ? idInput.value : '');

  if (variantId) {
    disableStackOptionsForVariant(card, variantId);
  }

  if (form instanceof HTMLFormElement) {
    let multiColor = false;
    const script = form.querySelector('script[data-options-block-variants]');
    if (script?.textContent) {
      try {
        const variants = JSON.parse(script.textContent);
        if (Array.isArray(variants)) {
          multiColor = stackVariantsHaveMultipleTier0Values(variants);
        }
      } catch {
        // ignore
      }
    }
    const lower = form.querySelector('.options-block__stack-lower-tiers');
    if (lower instanceof Element && !multiColor) {
      for (const input of lower.querySelectorAll('input[type="radio"][data-input-id]:checked')) {
        if (input instanceof HTMLInputElement && !isStackCartFailedControl(input)) {
          disableStackOptionInput(input);
        }
      }
    }
    clearStackDeepTierSelections(form);
  }

  if (idInput instanceof HTMLInputElement) {
    idInput.value = '';
    idInput.disabled = true;
  }

  const quick = card.querySelector('quick-add-component');
  if (quick) setIncompleteQuickAdd(quick);
  const atc = card.querySelector('add-to-cart-component');
  if (atc instanceof AddToCartComponent) atc.disable();

  card.removeAttribute('data-stack-notify-id');
  if (card instanceof HTMLElement) {
    delete card.dataset.stackRadioArrowNav;
    delete card.dataset.stackRadioKeyboardConfirm;
    if (variantId) card.dataset.stackCartFailVariantId = variantId;
  }

  queueMicrotask(() => {
    stackCartFailHandling.delete(card);
  });
}

/**
 * @param {Element} readScope
 * @param {number} keepIndex Clear option tiers with index greater than this (0-based)
 */
function clearDeeperOptionSelections(readScope, keepIndex) {
  for (const inp of readScope.querySelectorAll('input[type="radio"][data-input-id]')) {
    if (!(inp instanceof HTMLInputElement)) continue;
    const raw = inp.dataset.inputId;
    if (!raw) continue;
    const pos = parseInt(String(String(raw).split('-')[0] || '0'), 10) - 1;
    if (pos > keepIndex && inp.checked) {
      inp.checked = false;
      inp.dataset.currentChecked = 'false';
    }
  }
  for (const sel of readScope.querySelectorAll('select.variant-option__select')) {
    if (!(sel instanceof HTMLSelectElement)) continue;
    const o = sel.querySelector('option[data-input-id]');
    const raw = o?.getAttribute('data-input-id');
    if (!raw) continue;
    const pos = parseInt(String(String(raw).split('-')[0] || '0'), 10) - 1;
    if (pos > keepIndex) {
      sel.selectedIndex = -1;
    }
  }
}

/**
 * @param {Array<string|undefined>} slots
 * @param {OptionsBlockVariant[]} variants
 * @param {number} k
 */
function countMatchingPrefix(slots, variants, k) {
  return variants.filter((v) => {
    if (!v?.options) return false;
    for (let j = 0; j < k; j++) {
      if (!slotHasValue(slots[j])) return false;
      if (!sameOptionValue(v.options[j], slots[j])) return false;
    }
    return true;
  }).length;
}

/**
 * @param {Array<string|undefined>} slots
 * @param {OptionsBlockVariant[]} variants
 */
function countVariantsMatchingNonEmptySlots(slots, variants) {
  return variants.filter((v) => {
    const opts = v && v.options;
    if (!Array.isArray(opts)) return false;
    for (let j = 0; j < slots.length; j++) {
      if (!slotHasValue(slots[j])) continue;
      if (j >= opts.length) return false;
      if (!sameOptionValue(opts[j], slots[j])) return false;
    }
    return true;
  }).length;
}

/**
 * @param {string} s
 * @returns {string}
 */
function toPath(s) {
  if (!s || typeof s !== 'string') return '';
  const t = s.trim();
  if (!t) return '';
  try {
    return new URL(t, window.location.origin).pathname.replace(/\/$/, '');
  } catch {
    return '';
  }
}

/**
 * @param {Element} root
 * @returns {string}
 */
function pathFromBlock(root) {
  if (!root) return '';
  const vp =
    root instanceof Element
      ? root.tagName === 'VARIANT-PICKER'
        ? root
        : root.querySelector('variant-picker') ?? root.closest('variant-picker')
      : null;
  let uVp = '';
  if (vp) {
    uVp = (vp.getAttribute('data-product-url') || '').trim();
    if (!uVp && vp instanceof HTMLElement) {
      uVp = (vp.dataset.productUrl || '').trim();
    }
  }
  if (uVp) return toPath(uVp);
  const uSw = root
    .querySelector('product-swatches[data-product-url]')
    ?.getAttribute('data-product-url')
    ?.trim();
  if (uSw) return toPath(uSw);
  const aIn = root.querySelector('a.product-card__link[href*="/products/"]')?.getAttribute('href');
  if (aIn) return toPath(aIn);
  const card = root instanceof Element ? root.closest('product-card') : null;
  const aCard = card?.querySelector('a.product-card__link[href*="/products/"]')?.getAttribute('href');
  if (aCard) return toPath(aCard);
  return '';
}

/**
 * @param {Element} el
 * @param {string} path
 * @returns {boolean}
 */
function connectsElsewhere(el, path) {
  const raw = (el.getAttribute('data-connected-product-url') || '').trim();
  if (!raw) return false;
  const c = toPath(raw);
  if (!c) return false;
  if (!path) return true;
  return c !== path;
}

/**
 * Combined listing swatches navigate to other child products. Skip variant JSON constraints
 * unless this is the checked/selected value on the current product (size-aware availability).
 * @param {Element} el
 * @param {string} basePath
 * @returns {boolean}
 */
function skipCombinedListingConstraint(el, basePath) {
  const raw = (el.getAttribute('data-connected-product-url') || '').trim();
  if (!raw) return false;
  if (connectsElsewhere(el, basePath)) return true;
  if (el instanceof HTMLInputElement && el.type === 'radio' && !el.checked) return true;
  if (el instanceof HTMLOptionElement && !el.selected) return true;
  return false;
}

/**
 * @param {HTMLInputElement} input
 */
function applyCombinedListingServerAvailability(input) {
  const available = input.getAttribute('data-option-available') !== 'false';
  input.disabled = false;
  input.removeAttribute('aria-disabled');
  input.setAttribute('data-option-available', available ? 'true' : 'false');
  syncStackSwatchStrikethrough(input, available);
}

/**
 * @param {Element} el
 * @param {Element} card
 * @returns {boolean}
 */
function isCardHomeSwatch(el, card) {
  const homeUrl = (card.getAttribute('data-card-product-url') || '').trim();
  if (!homeUrl) return false;
  const connected = (el.getAttribute('data-connected-product-url') || '').trim();
  if (!connected) return false;
  return toPath(connected) === toPath(homeUrl);
}

/**
 * First available tier may be pre-checked in Liquid while variant JSON still belongs to the parent
 * card product; combined listings require variant-picker's section fetch (same as a real change).
 * @param {HTMLElement} stackRoot
 */
function maybeDispatchCombinedListingPreselSync(stackRoot) {
  const card = stackRoot.closest('product-card');
  if (!card) return;
  const form = resolveStackForm(null, stackRoot, card);
  if (!(form instanceof HTMLFormElement)) return;
  const checked = form.querySelector('input[type="radio"][data-fieldset-index="0"]:checked');
  if (!(checked instanceof HTMLInputElement)) return;
  const basePath = pathFromBlock(stackRoot);
  if (!connectsElsewhere(checked, basePath)) return;
  const connected = (checked.getAttribute('data-connected-product-url') || '').trim();
  if (!connected) return;
  checked.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * @param {Element | null | undefined} radioScope
 * @param {number} index
 * @returns {boolean}
 */
function isErpMaskedSlot(radioScope, index) {
  if (!(radioScope instanceof Element)) return false;
  const esc = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(String(index)) : String(index);
  return Boolean(
    radioScope.querySelector(
      `[data-erp-masked-option="true"][data-fieldset-index="${esc}"]`
    )
  );
}

/**
 * @param {Element | null | undefined} scope
 * @param {number} fieldsetIndex
 * @returns {number}
 */
function slotIndexFromFieldsetIndex(scope, fieldsetIndex) {
  if (!(scope instanceof Element)) return fieldsetIndex;
  const esc =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(String(fieldsetIndex))
      : String(fieldsetIndex);
  const fs = scope.querySelector(`fieldset[data-fieldset-index="${esc}"]`);
  const sample = fs?.querySelector('input[type="radio"][data-input-id]');
  if (sample instanceof HTMLInputElement) {
    const idx = slotIndexFromRadio(sample);
    if (idx >= 0) return idx;
  }
  return fieldsetIndex;
}

/**
 * @param {Element} radioScope
 * @param {Array<string|undefined>} slots
 */
function syncErpMaskedOptions(radioScope, slots) {
  if (!(radioScope instanceof Element)) return;

  for (const maskedEl of radioScope.querySelectorAll('[data-erp-masked-option="true"][data-erp-sync-from-fieldset-index]')) {
    const fromFieldsetIdx = parseInt(maskedEl.getAttribute('data-erp-sync-from-fieldset-index') || '', 10);
    if (Number.isNaN(fromFieldsetIdx)) continue;
    const fromIdx = slotIndexFromFieldsetIndex(radioScope, fromFieldsetIdx);
    if (fromIdx < 0 || fromIdx >= slots.length) continue;

    const fromValue = slots[fromIdx];
    if (!slotHasValue(fromValue)) continue;
    const want = optStr(fromValue);

    let maskedIdx = parseInt(maskedEl.getAttribute('data-fieldset-index') || '', 10);
    if (Number.isNaN(maskedIdx)) {
      const sample =
        maskedEl.querySelector('input[type="radio"][data-input-id]') ??
        maskedEl.querySelector('select.variant-option__select option[data-input-id]');
      const raw =
        sample instanceof HTMLInputElement
          ? sample.dataset.inputId
          : sample instanceof HTMLOptionElement
            ? sample.getAttribute('data-input-id')
            : null;
      if (raw) {
        maskedIdx = parseInt(String(raw).split('-')[0] || '0', 10) - 1;
      }
    }
    if (Number.isNaN(maskedIdx) || maskedIdx < 0 || maskedIdx >= slots.length) continue;

    const wantKey = normalizeOptionValueKey(want);
    const maskedSelect = maskedEl.querySelector('select.variant-option__select');
    if (maskedSelect instanceof HTMLSelectElement) {
      let matched = null;
      for (const option of maskedSelect.options) {
        if (normalizeOptionValueKey(option.value) === wantKey) {
          matched = option;
          break;
        }
      }
      if (matched) {
        for (const option of maskedSelect.options) {
          option.removeAttribute('selected');
        }
        matched.setAttribute('selected', 'selected');
        maskedSelect.value = matched.value;
        slots[maskedIdx] = matched.value;
      }
      continue;
    }

    for (const input of maskedEl.querySelectorAll('input[type="radio"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (normalizeOptionValueKey(input.value) === wantKey) {
        input.checked = true;
        input.dataset.currentChecked = 'true';
        slots[maskedIdx] = input.value;
      } else if (input.checked) {
        input.checked = false;
        input.dataset.currentChecked = 'false';
      }
    }
  }
}

/**
 * @param {Array<string|undefined>} slots
 * @param {OptionsBlockVariant[]} variants
 * @param {Element | null | undefined} [radioScope]
 * @returns {OptionsBlockVariant[]}
 */
function matchingWhenComplete(slots, variants, radioScope) {
  for (let i = 0; i < slots.length; i++) {
    if (!slotHasValue(slots[i])) {
      return [];
    }
  }
  return variants.filter(
    /**
     * @param {OptionsBlockVariant} v
     * @param {number} _i
     * @param {OptionsBlockVariant[]} _arr
     */
    (v, _i, _arr) => {
      if (!v?.options || v.options.length !== slots.length) return false;
      for (let i = 0; i < slots.length; i++) {
        if (!sameOptionValue(v.options[i], slots[i])) return false;
      }
      return true;
    }
  );
}

/**
 * @param {OptionsBlockVariant[]} variants
 * @param {number} pos
 * @param {string} value
 * @param {Array<string|undefined|null>} slots
 * @param {number} n
 */
function hasAvailableConstrainedOption(variants, pos, value, slots, n) {
  const vWant = optStr(value);
  for (const v of variants) {
    if (!v || v.available !== true || !v.options || v.options.length < n) continue;
    if (!sameOptionValue(v.options[pos], vWant)) continue;
    let match = true;
    for (let j = 0; j < n; j++) {
      if (j === pos) continue;
      if (!slotHasValue(slots[j])) continue;
      if (!sameOptionValue(v.options[j], slots[j])) {
        match = false;
        break;
      }
    }
    if (match) return true;
  }
  return false;
}

/**
 * @param {HTMLInputElement} input
 * @param {boolean} available
 */
function syncStackSwatchStrikethrough(input, available) {
  const lab = input.closest('label.variant-option__button-label--has-swatch');
  if (!(lab instanceof HTMLElement)) return;
  if (available) {
    for (const svg of lab.querySelectorAll('svg.variant-option__strikethrough')) {
      svg.remove();
    }
    return;
  }
  if (lab.querySelector('svg.variant-option__strikethrough')) return;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('width', '24');
  svg.setAttribute('height', '24');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('class', 'variant-option__strikethrough');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('data-stack-dynamic-strikethrough', 'true');
  const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M0 24L24 0');
  path.setAttribute('stroke', 'currentcolor');
  path.setAttribute('stroke-width', '1.5');
  g.appendChild(path);
  svg.appendChild(g);
  lab.appendChild(svg);
}

/**
 * @param {Element} readScope
 * @param {OptionsBlockVariant[]} variants
 * @param {Array<string|undefined|null>} slots
 * @param {number} n
 * @param {number} [minTierIndex]
 * @returns {boolean}
 */
function updateConstrainedOptionAvailability(readScope, variants, slots, n, minTierIndex = 0) {
  const basePath = pathFromBlock(readScope);
  const card = readScope.closest('product-card');
  let cleared = false;
  const allRadios = readScope.querySelectorAll('input[type="radio"][data-input-id]');
  for (const el of allRadios) {
    if (!(el instanceof HTMLInputElement)) continue;
    if (isStackCartFailedControl(el)) continue;
    const raw = el.dataset.inputId;
    if (!raw) continue;
    const pos = parseInt(String(String(raw).split('-')[0] || '0'), 10) - 1;
    if (pos < 0 || pos >= n) continue;
    if (pos < minTierIndex) continue;

    if (card instanceof Element && isCardHomeSwatch(el, card)) {
      const homeAvailable = card.getAttribute('data-card-product-available') !== 'false';
      el.disabled = false;
      el.removeAttribute('aria-disabled');
      el.setAttribute('data-option-available', homeAvailable ? 'true' : 'false');
      syncStackSwatchStrikethrough(el, homeAvailable);
      continue;
    }

    if (skipCombinedListingConstraint(el, basePath)) {
      applyCombinedListingServerAvailability(el);
      continue;
    }

    const val = el.value;
    const canBuy = hasAvailableConstrainedOption(variants, pos, val, slots, n);
    if (canBuy) {
      el.disabled = false;
      el.setAttribute('data-option-available', 'true');
      el.removeAttribute('aria-disabled');
      syncStackSwatchStrikethrough(el, true);
    } else {
      el.disabled = true;
      el.setAttribute('data-option-available', 'false');
      el.setAttribute('aria-disabled', 'true');
      syncStackSwatchStrikethrough(el, false);
      if (el.checked) {
        el.checked = false;
        el.dataset.currentChecked = 'false';
        cleared = true;
      }
    }
  }

  for (const sel of readScope.querySelectorAll('select.variant-option__select')) {
    if (!(sel instanceof HTMLSelectElement)) continue;
    const firstWithId = sel.querySelector('option[data-input-id]');
    const raw0 = firstWithId?.getAttribute('data-input-id');
    if (!raw0) continue;
    const pos = parseInt(String(String(raw0).split('-')[0] || '0'), 10) - 1;
    if (pos < 0 || pos >= n) continue;
    if (pos < minTierIndex) continue;
    for (const opt of Array.from(sel.options)) {
      const r = opt.getAttribute('data-input-id');
      if (!r) continue;
      if (isStackCartFailedControl(opt)) continue;
      const oVal = opt.value;

      if (card instanceof Element && isCardHomeSwatch(opt, card)) {
        const homeAvailable = card.getAttribute('data-card-product-available') !== 'false';
        opt.disabled = false;
        opt.setAttribute('data-option-available', homeAvailable ? 'true' : 'false');
        continue;
      }

      if (skipCombinedListingConstraint(/** @type {Element} */ (opt), basePath)) {
        const available = opt.getAttribute('data-option-available') !== 'false';
        opt.disabled = false;
        opt.setAttribute('data-option-available', available ? 'true' : 'false');
        continue;
      }

      const can = hasAvailableConstrainedOption(variants, pos, oVal, slots, n);
      opt.disabled = !can;
      if (!can && opt.selected) {
        opt.selected = false;
        cleared = true;
      }
    }
  }
  return cleared;
}

/**
 * @param {Element | null | undefined} quick
 */
function optionCountFromQuick(quick) {
  const raw = quick?.getAttribute('data-product-options-count');
  if (raw == null || raw === '') return 0;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * @param {Element | null | undefined} card
 * @returns {boolean}
 */
function cardUsesMobileModalQuickAdd(card) {
  if (!(card instanceof Element)) return false;
  if (!isMobileBreakpoint()) return false;
  return card.getAttribute('data-mobile-modal-quick-add') === 'true';
}

/**
 * @param {Element | null | undefined} card
 * @returns {boolean}
 */
function cardQuickAddDesktopOnlyOnMobile(card) {
  if (!(card instanceof Element)) return false;
  if (!isMobileBreakpoint()) return false;
  return card.getAttribute('data-quick-add-desktop-only') === 'true';
}

/**
 * @param {Element | null | undefined} card
 * @returns {boolean}
 */
function cardHasOneSizeTier(card) {
  return Boolean(card && card.querySelector('[data-one-size-input]'));
}

/**
 * @param {Element} card
 * @param {Element} source
 */
function submitCardQuickAddFromOneSize(card, source) {
  const quick = card.querySelector('quick-add-component');
  if (!(quick instanceof HTMLElement)) return;
  const form = quick.querySelector('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return;
  const idIn = form.querySelector('input[name="id"]');
  if (!(idIn instanceof HTMLInputElement) || !idIn.value || idIn.disabled) return;
  const flyFrom = stackFlySourceForChange(card, source);
  queueMicrotask(() => {
    const atc = quick.querySelector('add-to-cart-component');
    if (atc instanceof AddToCartComponent) {
      atc.playAddToCartAnimations(quick, flyFrom ?? undefined);
    }
    form.requestSubmit();
  });
}

/**
 * Product cards: never expand 2nd/3rd option tiers when (a) mobile, or (b) product has 3+ option dimensions.
 * @param {Element | null | undefined} card
 * @returns {boolean}
 */
function productCardFreezesDeepStackTiers(card) {
  if (!(card instanceof Element)) return false;
  if (card.tagName !== 'PRODUCT-CARD') return false;
  if (card.getAttribute('data-freeze-stack-deep-tiers') === 'true') return true;
  return isMobileBreakpoint();
}

/**
 * @param {Element} card
 * @param {Element | null | undefined} target
 */
function stackFlySourceForChange(card, target) {
  if (!target || !card.contains(target)) {
    return (
      card.querySelector('slideshow-component img, .card-gallery img, [data-view-transition-to-main-product] img') ||
      card.querySelector('.options-block--opt-stack') ||
      card
    );
  }
  if (target instanceof HTMLInputElement && target.id) {
    // Shopify ids may contain characters that aren't valid in an unescaped
    // attribute selector (colons, dots, leading digits). Use CSS.escape when
    // available, otherwise fall back to an iteration that doesn't go through
    // selector parsing at all to avoid SyntaxError.
    /** @type {Element | null} */
    let lab = null;
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      lab = card.querySelector(`label[for="${CSS.escape(target.id)}"]`);
    } else {
      lab =
        Array.from(card.querySelectorAll('label[for]')).find(
          (el) => el instanceof HTMLLabelElement && el.htmlFor === target.id
        ) ?? null;
    }
    if (lab) return lab;
  }
  const label = target.closest('label');
  if (label && card.contains(label)) {
    return label;
  }
  if (target instanceof HTMLSelectElement) {
    return target;
  }
  if (target.matches?.('input, button, .variant-option__button-label')) {
    return target;
  }
  return (
    card.querySelector('slideshow-component img, .card-gallery img, [data-view-transition-to-main-product] img') ||
    card.querySelector('.options-block--opt-stack') ||
    card
  );
}

/**
 * @param {Element | null | undefined} quick
 */
function setIncompleteQuickAdd(quick) {
  if (!quick) return;
  const card = quick.closest('product-card');
  const stackRoot = card?.querySelector('.options-block--opt-stack');
  const form = stackRoot?.querySelector('form.variant-picker__form, form.options-block--stack-form');
  if (tryApplySingleVariantQuickAdd(card, stackRoot, form instanceof HTMLFormElement ? form : null)) {
    return;
  }
  const oc = optionCountFromQuick(quick);
  if (oc === 0) {
    quick.setAttribute('data-quick-add-button', 'add');
    return;
  }
  if (oc >= 2) {
    quick.setAttribute('data-quick-add-button', 'choose');
    return;
  }
  if (
    quick.getAttribute('data-inline-quick-add') === 'true' ||
    quick.getAttribute('data-require-explicit-swatch') === 'true' ||
    quick.getAttribute('data-inline-swatch-picked') === 'true'
  ) {
    quick.setAttribute('data-quick-add-button', 'add');
    return;
  }
  quick.setAttribute('data-quick-add-button', 'choose');
}

/**
 * @param {Element | null} card
 * @param {OptionsBlockVariant} v
 * @param {boolean} isUser
 * @param {Element | null | undefined} fromEl
 * @param {boolean} [bypassKeyboardConfirmGuard]
 * @returns {boolean}
 */
function tryStackAutoAddToCart(card, v, isUser, fromEl, bypassKeyboardConfirmGuard = false) {
  if (!isUser || !card || v == null || v.id == null) return false;
  if (cardUsesMobileModalQuickAdd(card)) return false;
  if (cardQuickAddDesktopOnlyOnMobile(card)) return false;
  if (cardHasOneSizeTier(card)) return false;
  if (v.available === false) return false;
  if (card instanceof HTMLElement && card.dataset.stackRadioArrowNav === '1') {
    queueMicrotask(() => {
      delete card.dataset.stackRadioArrowNav;
    });
    return false;
  }
  if (
    card instanceof HTMLElement &&
    card.dataset.stackRadioKeyboardConfirm === '1' &&
    !bypassKeyboardConfirmGuard
  ) {
    return false;
  }
  const stackRoot = card.querySelector('.options-block--opt-stack');
  if (!stackRoot) return false;
  const script =
    stackRoot.querySelector('script[data-options-block-variants]') ??
    card.querySelector('script[data-options-block-variants]');
  const quick = card.querySelector('quick-add-component');
  const optionCount = optionCountFromScript(script) || optionCountFromQuick(quick);
  if (!quick || optionCount < 2) return false;
  const form = quick.querySelector('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return false;
  const idIn = form.querySelector('input[name="id"]');
  if (idIn instanceof HTMLInputElement && idIn.disabled) return false;

  const isSingleVariant = scriptVariantCount(script) === 1;
  const nOpt = v.options?.length ?? optionCountFromScript(script);
  /** @type {Element | null | undefined} */
  let submitFrom = fromEl;
  if (!isSingleVariant && nOpt >= 1 && fromEl) {
    const ch = optionIndexFromControl(fromEl);
    if (ch != null && ch !== nOpt - 1) {
      const slotReadScope = stackRoot instanceof HTMLElement ? stackRoot : card;
      const lastCtrl = findCheckedControlOnTier(slotReadScope, nOpt - 1);
      if (!lastCtrl) return false;
      submitFrom = lastCtrl;
    }
  } else if (isSingleVariant && optionCount >= 2 && fromEl) {
    const ch = optionIndexFromControl(fromEl);
    if (ch == null || ch !== nOpt - 1) return false;
  }

  const flyFrom = stackFlySourceForChange(card, submitFrom);
  if (card instanceof HTMLElement && v.id != null) {
    card.dataset.stackLastSubmitVariantId = String(v.id);
  }
  queueMicrotask(() => {
    const atc = quick.querySelector('add-to-cart-component');
    if (atc instanceof AddToCartComponent) {
      atc.enable();
      atc.playAddToCartAnimations(quick, flyFrom ?? undefined);
    }
    form.requestSubmit();
  });
  return true;
}

/**
 * @param {Element} start
 * @param {boolean} [isUser]
 * @param {number} [depth]
 * @param {Element | null} [fromEl]
 * @param {number} [availMinTier]
 */
function runRoot(start, isUser = false, depth = 0, fromEl = null, availMinTier) {
  const card = start?.closest('product-card') ?? null;
  if (cardUsesMobileModalQuickAdd(card)) {
    const quick = card?.querySelector('quick-add-component');
    if (quick) quick.setAttribute('data-quick-add-button', 'choose');
    return;
  }
  const stackRoot = resolveStackRoot(
    card,
    start instanceof Element ? start : null,
    fromEl instanceof Element ? fromEl : null
  );
  const form = resolveStackForm(
    fromEl instanceof Element ? fromEl : null,
    stackRoot,
    card
  );

  if (!stackRoot) {
    return;
  }
  let script = form?.querySelector('script[data-options-block-variants]') ?? null;
  if (!script || !script.textContent) {
    script = stackRoot.querySelector('script[data-options-block-variants]');
  }

  if (!form || !script?.textContent) {
    return;
  }

  let variants;
  try {
    variants = JSON.parse(script.textContent);
  } catch {
    return;
  }
  if (!Array.isArray(variants)) return;
  for (const v0 of variants) {
    ensureVariantOptionsNormalized(v0);
  }

  if (
    isUser &&
    fromEl instanceof Element &&
    form instanceof HTMLFormElement &&
    optionIndexFromControl(fromEl) === 0
  ) {
    clearStackLowerTierCartFailedFlags(form);
  }

  const readScope = card ?? stackRoot ?? start;
  if (!(readScope instanceof Element)) return;

  if (form instanceof HTMLFormElement) {
    syncStackRadioCheckedVisuals(form);
  }

  const forSkip = /** @type {HTMLElement | null} */ (stackRoot ?? card?.querySelector('.options-block--opt-stack'));
  const slotForm = form instanceof HTMLFormElement ? form : readScope;
  const slotReadScope =
    card instanceof Element ? card : stackRoot instanceof HTMLElement ? stackRoot : slotForm;

  const fullSlots = readSlotsForMatching(slotReadScope, script, fromEl, slotForm);
  const quick = card?.querySelector('quick-add-component');
  const idInput = quick?.querySelector('form input[name="id"]');
  const atc = quick?.querySelector('add-to-cart-component');

  if (variants.length === 1) {
    const v = variants[0];
    if (v != null) {
      if (stackFormHasCartFailedLowerTierOptions(form)) {
        if (idInput instanceof HTMLInputElement) {
          idInput.value = '';
          idInput.disabled = true;
        }
        if (quick) {
          setIncompleteQuickAdd(quick);
        }
        if (atc instanceof AddToCartComponent) {
          atc.disable();
        }
        return;
      }
      if (idInput instanceof HTMLInputElement) {
        idInput.value = String(v.id);
        idInput.disabled = v.available === false;
      }
      if (quick) {
        quick.setAttribute('data-quick-add-button', 'add');
      }
      if (atc instanceof AddToCartComponent) {
        if (v.available) {
          atc.enable();
        } else {
          atc.disable();
        }
      }
      syncAddToCartFlyImage(quick, v);
      if (isUser) {
        tryStackAutoAddToCart(card, v, true, fromEl);
      }
      return;
    }
  }

  const slotN = fullSlots.length;
  let nSet = 0;
  for (let i = 0; i < fullSlots.length; i++) {
    if (slotHasValue(fullSlots[i])) nSet++;
  }
  const loose = slotN === 2 ? countVariantsMatchingNonEmptySlots(fullSlots, variants) : -1;
  const freezeDeepTiers = productCardFreezesDeepStackTiers(card);
  const deepTiersUseCardHoverOnly =
    card instanceof Element && card.matches('product-card');
  for (let k = 1; k < fullSlots.length; k++) {
    const fs = form.querySelector(`fieldset.options-block-stack--pending[data-fieldset-index="${k}"]`);
    if (!fs) continue;
    let show;
    if (!deepTiersUseCardHoverOnly) {
      if (slotN === 2) {
        show = nSet > 0 && loose >= 1;
      } else {
        const c = countMatchingPrefix(fullSlots, variants, k);
        let prevOk = true;
        for (let j = 0; j < k; j++) {
          if (!slotHasValue(fullSlots[j])) {
            prevOk = false;
            break;
          }
        }
        show = Boolean(prevOk && c >= 1);
      }
      if (freezeDeepTiers) {
        show = false;
      }
      fs.classList.toggle('options-block-stack--open', show);
    } else {
      fs.classList.remove('options-block-stack--open');
    }
  }

  const gateHost = stackRoot ?? card;
  const swatchGate = gateHost?.querySelector('.options-block__swatch-gate[data-stack-swatch-gate-index]');
  if (swatchGate instanceof HTMLElement) {
    const raw = swatchGate.getAttribute('data-stack-swatch-gate-index');
    const gIdx = raw == null ? NaN : parseInt(raw, 10);
    if (!Number.isNaN(gIdx) && gIdx > 0) {
      if (!deepTiersUseCardHoverOnly) {
        let gateShow;
        if (slotN === 2) {
          gateShow = nSet > 0 && loose >= 1;
        } else {
          let gPrevOk = true;
          for (let j = 0; j < gIdx; j++) {
            if (!slotHasValue(fullSlots[j])) {
              gPrevOk = false;
              break;
            }
          }
          const gc = countMatchingPrefix(fullSlots, variants, gIdx);
          gateShow = Boolean(gPrevOk && gc >= 1);
        }
        if (freezeDeepTiers) {
          gateShow = false;
        }
        swatchGate.classList.toggle('options-block-stack--open', gateShow);
      } else {
        swatchGate.classList.remove('options-block-stack--open');
      }
    }
  }

  const oneSizeFs = form?.querySelector('fieldset[data-one-size-tier]');
  if (oneSizeFs instanceof HTMLElement && slotN >= 1) {
    if (!deepTiersUseCardHoverOnly) {
      const openOne = Boolean(slotHasValue(fullSlots[0]));
      oneSizeFs.classList.toggle('options-block-stack--open', openOne);
    } else {
      oneSizeFs.classList.remove('options-block-stack--open');
    }
  }

  /**
   * @param {Array<string|undefined>} slotsForMatch
   */
  const completeMatchingAndQuickAdd = (slotsForMatch) => {
    const list = matchingWhenComplete(slotsForMatch, variants, slotForm);

    if (list.length === 1) {
      const v = list[0];
      if (v != null) {
        if (idInput instanceof HTMLInputElement) {
          idInput.value = String(v.id);
          idInput.disabled = v.available === false;
        }
        if (quick) {
          quick.setAttribute('data-quick-add-button', 'add');
        }
        if (atc instanceof AddToCartComponent) {
          if (v.available) {
            atc.enable();
          } else {
            atc.disable();
          }
        }
        syncAddToCartFlyImage(quick, v);
        tryStackAutoAddToCart(card, v, isUser, fromEl);
        if (forSkip?.getAttribute('data-stack-skip-section-fetch')) {
          forSkip.removeAttribute('data-stack-skip-section-fetch');
          if (v.id) card?.setAttribute('data-stack-notify-id', String(v.id));
        } else if (card && v.id) {
          const mark = card.getAttribute('data-stack-notify-id');
          if (String(v.id) !== mark) {
            const picker = card.querySelector('variant-picker');
            if (picker instanceof HTMLElement) {
              const productUrl = picker.dataset.productUrl;
              if (card.hasAttribute('data-card-variant-section-fetch')) {
                card.setAttribute('data-stack-notify-id', String(v.id));
              } else if (productUrl) {
                const notifyId = String(v.id);
                card.setAttribute('data-stack-notify-id', notifyId);
                const base = sectionRenderingProductPathname(v, productUrl, fromEl);
                const u = `${base}?section_id=section-rendering-product-card&variant=${v.id}`;
                const rollbackMark = () => {
                  if (card?.getAttribute('data-stack-notify-id') === notifyId) {
                    card.removeAttribute('data-stack-notify-id');
                  }
                };
                const prevFetch = stackCardSectionFetch.get(card);
                if (prevFetch) prevFetch.abort();
                const ac = new AbortController();
                stackCardSectionFetch.set(card, ac);
                fetch(u, { signal: ac.signal })
                  .then((r) => (r.ok ? r.text() : null))
                  .then((text) => {
                    if (!text) {
                      if (card.getAttribute('data-stack-notify-id') === notifyId) rollbackMark();
                      return;
                    }
                    if (card.getAttribute('data-stack-notify-id') !== notifyId) return;
                    try {
                      const doc = new DOMParser().parseFromString(text, 'text/html');
                      const jp = (
                        doc.querySelector(
                          'variant-picker script[type="application/json"]:not([data-options-block-variants])'
                        ) ?? doc.querySelector('variant-picker script[type="application/json"]')
                      )?.textContent;
                      if (!jp) {
                        if (card.getAttribute('data-stack-notify-id') === notifyId) rollbackMark();
                        return;
                      }
                      const vr = JSON.parse(jp);
                      if (card.getAttribute('data-stack-notify-id') !== notifyId) return;
                      const sourceId = stackVariantUpdateSourceId(picker, card, fromEl);
                      picker.dispatchEvent(
                        new VariantUpdateEvent(vr, sourceId, {
                          html: doc,
                          productId: picker.dataset.productId ?? '',
                        })
                      );
                    } catch (err) {
                      if (card.getAttribute('data-stack-notify-id') === notifyId) rollbackMark();
                      console.warn('Stack section response parse failed', err);
                    }
                  })
                  .catch((err) => {
                    if (err.name === 'AbortError') return;
                    if (card.getAttribute('data-stack-notify-id') === notifyId) rollbackMark();
                    console.warn('Stack section fetch failed', err);
                  })
                  .finally(() => {
                    if (stackCardSectionFetch.get(card) === ac) {
                      stackCardSectionFetch.delete(card);
                    }
                  });
              }
            }
          }
        }
      }
    } else {
      card?.removeAttribute('data-stack-notify-id');
      if (idInput instanceof HTMLInputElement) {
        idInput.value = '';
        idInput.disabled = true;
      }
      if (quick) {
        setIncompleteQuickAdd(quick);
      }
      if (atc instanceof AddToCartComponent) {
        atc.disable();
      }
    }
  };

  const dim = fullSlots.length;
  if (dim > 0 && depth < 10) {
    const changedTierIndex =
      fromEl instanceof Element ? optionIndexFromControl(fromEl) : null;
    const fromLowerTiers =
      fromEl instanceof Element &&
      Boolean(fromEl.closest('.options-block__stack-lower-tiers'));
    let minTierIndexForAvailability = 0;
    if (fromLowerTiers) {
      minTierIndexForAvailability =
        changedTierIndex != null ? Math.max(1, changedTierIndex) : 1;
    } else if (changedTierIndex != null && changedTierIndex >= 1) {
      minTierIndexForAvailability = changedTierIndex;
    }
    if (typeof availMinTier === 'number') {
      minTierIndexForAvailability = Math.max(minTierIndexForAvailability, availMinTier);
    }
    const availabilityReadScope =
      forSkip instanceof HTMLElement ? forSkip : slotForm;
    const reapply = () => {
      const s = readSlotsForMatching(slotReadScope, script, fromEl, slotForm);
      return updateConstrainedOptionAvailability(
        availabilityReadScope,
        variants,
        s,
        dim,
        minTierIndexForAvailability
      );
    };
    if (reapply()) {
      return runRoot(start, isUser, depth + 1, fromEl, availMinTier);
    }
    queueMicrotask(() => {
      if (reapply()) {
        runRoot(start, isUser, depth + 1, fromEl, availMinTier);
        return;
      }
      requestAnimationFrame(() => {
        if (reapply()) {
          runRoot(start, isUser, depth + 1, fromEl, availMinTier);
          return;
        }
        completeMatchingAndQuickAdd(readSlotsForMatching(slotReadScope, script, fromEl, slotForm));
      });
    });
    return;
  }

  completeMatchingAndQuickAdd(fullSlots);
}

/**
 * @param {Element} root
 */
function clearQuickOnEnter(root) {
  const card = root.closest('product-card');
  const quick = card?.querySelector('quick-add-component');
  if (!quick) return;
  const form = root.querySelector('form.variant-picker__form, form.options-block--stack-form');
  if (tryApplySingleVariantQuickAdd(card, root, form instanceof HTMLFormElement ? form : null)) {
    return;
  }
  const idInput = quick.querySelector('form input[name="id"]');
  if (idInput instanceof HTMLInputElement) {
    idInput.value = '';
    idInput.disabled = true;
  }
  setIncompleteQuickAdd(quick);
  const atc = quick.querySelector('add-to-cart-component');
  if (atc instanceof AddToCartComponent) {
    atc.disable();
  }
}

/** @param {Event} e */
function onChange(e) {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const card = target.closest('product-card');
  if (!card) return;
  if (cardUsesMobileModalQuickAdd(card)) return;
  const stackRoot =
    target.closest('.options-block--opt-stack') ||
    (card && card.querySelector('.options-block--opt-stack'));
  if (!stackRoot) return;

  if (target instanceof HTMLInputElement && target.matches('[data-one-size-input]') && target.checked) {
    if (card instanceof HTMLElement) {
      submitCardQuickAddFromOneSize(card, target);
    }
    return;
  }
  if (card) {
    const inRelevant = target.closest(
      'variant-picker, swatches-variant-picker-component, product-swatches, .options-block--opt-stack'
    );
    if (!inRelevant) return;
  }
  const clearForm = resolveStackForm(
    target,
    stackRoot instanceof HTMLElement ? stackRoot : null,
    card
  );
  if (clearForm?.querySelector('[data-one-size-input]')) {
    if (!(target instanceof HTMLInputElement && target.matches('[data-one-size-input]'))) {
      for (const os of clearForm.querySelectorAll('[data-one-size-input]')) {
        if (os instanceof HTMLInputElement && os !== target) {
          os.checked = false;
        }
      }
    }
  }
  const scriptForN =
    stackRoot.querySelector('script[data-options-block-variants]') ||
    (clearForm && clearForm.querySelector('script[data-options-block-variants]')) ||
    (card && card.querySelector('script[data-options-block-variants]'));
  if (scriptForN?.textContent) {
    try {
      const n = optionCountFromScript(scriptForN);
      if (n > 1) {
        const idx = optionIndexFromControl(target);
        if (idx != null && idx < n - 1 && clearForm) {
          clearDeeperOptionSelections(clearForm, idx);
        }
      }
    } catch {
    }
  }
  if (stackRoot instanceof HTMLElement && target instanceof HTMLInputElement) {
    scheduleStackOptionUserActivate(stackRoot, target);
    return;
  }
  queueMicrotask(() => {
    runRoot(stackRoot, true, 0, target);
  });
}

/** @param {import('@theme/events').VariantUpdateEvent} e */
function onVariantUpdate(e) {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const card = target.closest('product-card');
  if (!card) return;
  if (cardUsesMobileModalQuickAdd(card)) return;
  const root =
    target.closest('.options-block--opt-stack') || card.querySelector('.options-block--opt-stack');
  if (!root) return;
  const stackForm = resolveStackForm(null, root instanceof HTMLElement ? root : null, card);
  if (stackFormHasCartFailedLowerTierOptions(stackForm)) {
    return;
  }
  if (e.detail?.data?.html) {
    root.setAttribute('data-stack-skip-section-fetch', '1');
  }
  setTimeout(() => {
    runRoot(root, false, 0, null, 1);
    syncExplicitSwatchIfStackPreselected(card);
  }, 0);
}

/**
 * Stack mode may pre-check the first available tier-1 value in Liquid; quick-add still expects
 * `data-inline-swatch-picked` when `data-require-explicit-swatch` is set (single-option cards).
 * @param {ParentNode} [scope]
 */
function syncExplicitSwatchIfStackPreselected(scope = document) {
  for (const quick of scope.querySelectorAll('quick-add-component[data-require-explicit-swatch="true"]')) {
    if (!(quick instanceof HTMLElement)) continue;
    const card = quick.closest('product-card');
    const form =
      card instanceof HTMLElement ? card.querySelector('form.options-block--stack-form') : null;
    if (!(form instanceof HTMLFormElement)) continue;
    if (form.querySelector('input[type="radio"][data-input-id]:checked')) {
      quick.setAttribute('data-inline-swatch-picked', 'true');
    }
  }
}

/** @param {KeyboardEvent} e */
function onStackRadioKeydown(e) {
  const target = e.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
  const stack = target.closest('.options-block--opt-stack');
  if (!(stack instanceof HTMLElement)) return;
  const card = stack.closest('product-card');
  if (!(card instanceof HTMLElement)) return;
  const key = e.key;
  if (
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'Home' ||
    key === 'End'
  ) {
    card.dataset.stackRadioArrowNav = '1';
    return;
  }
  if (key !== 'Enter' && key !== ' ') return;
  const form = resolveStackForm(target, stack, card);
  if (!(form instanceof HTMLFormElement)) return;
  let script = form.querySelector('script[data-options-block-variants]');
  if (!script?.textContent) {
    script = stack.querySelector('script[data-options-block-variants]');
  }
  if (!script?.textContent) return;
  let variants;
  try {
    variants = JSON.parse(script.textContent);
  } catch {
    return;
  }
  if (!Array.isArray(variants)) return;
  for (const v0 of variants) {
    ensureVariantOptionsNormalized(v0);
  }
  const optionCount = optionCountFromScript(script);
  if (optionCount < 1) return;
  const tierIdx = optionIndexFromControl(target);
  if (tierIdx == null || tierIdx !== optionCount - 1) return;
  let didSelectForKeyboardConfirm = false;
  if (!target.checked) {
    card.dataset.stackRadioKeyboardConfirm = '1';
    target.checked = true;
    syncStackRadioCheckedVisuals(form);
    didSelectForKeyboardConfirm = true;
  }
  runRoot(stack, false, 0, target);
  const slotReadScope = card instanceof Element ? card : stack;
  const slots = readSlotsForMatching(slotReadScope, script, target, form);
  const list = matchingWhenComplete(slots, variants, form);
  if (list.length !== 1) {
    if (didSelectForKeyboardConfirm) {
      queueMicrotask(() => {
        delete card.dataset.stackRadioKeyboardConfirm;
      });
    }
    return;
  }
  const variant = list[0];
  if (variant == null) {
    if (didSelectForKeyboardConfirm) {
      queueMicrotask(() => {
        delete card.dataset.stackRadioKeyboardConfirm;
      });
    }
    return;
  }
  delete card.dataset.stackRadioArrowNav;
  if (tryStackAutoAddToCart(card, variant, true, target, true)) {
    e.preventDefault();
  }
  if (didSelectForKeyboardConfirm) {
    queueMicrotask(() => {
      delete card.dataset.stackRadioKeyboardConfirm;
    });
  }
}

/** @type {WeakMap<HTMLElement, true>} */
const stackOptionActivatePending = new WeakMap();

/**
 * @param {Element} target
 * @returns {HTMLInputElement | null}
 */
function resolveStackFormRadioInput(target) {
  if (!(target instanceof Element)) return null;
  const input =
    target instanceof HTMLInputElement && target.type === 'radio'
      ? target
      : target.closest('label')?.querySelector('input[type="radio"][data-input-id]');
  if (!(input instanceof HTMLInputElement)) return null;
  if (!input.closest('form.options-block--stack-form')) return null;
  if (input.disabled) return null;
  return input;
}

/**
 * @param {HTMLElement} stack
 * @param {HTMLInputElement} input
 */
function scheduleStackOptionUserActivate(stack, input) {
  const card = stack.closest('product-card');
  if (!(card instanceof HTMLElement)) return;
  if (stackOptionActivatePending.has(card)) return;
  stackOptionActivatePending.set(card, true);
  queueMicrotask(() => {
    stackOptionActivatePending.delete(card);
    runRoot(stack, true, 0, input);
  });
}

/** @param {Event} e */
function onStackFormOptionActivate(e) {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const card = target.closest('product-card');
  if (!(card instanceof HTMLElement)) return;
  if (!card.querySelector('.options-block--opt-stack')) return;
  if (card.dataset.stackRadioArrowNav === '1') return;
  if (card.dataset.stackRadioKeyboardConfirm === '1') return;

  const input = resolveStackFormRadioInput(target);
  if (!input) return;
  if (input.disabled) return;
  if (!input.checked && input.dataset.currentChecked === 'true') {
    input.dataset.currentChecked = 'false';
  }
  if (input.checked) return;

  const stack = input.closest('.options-block--opt-stack');
  if (!(stack instanceof HTMLElement)) return;

  scheduleStackOptionUserActivate(stack, input);
}

function init() {
  for (const el of document.querySelectorAll('.options-block--opt-stack')) {
    if (!(el instanceof HTMLElement)) continue;
    clearQuickOnEnter(el);
    runRoot(el, false);
    queueMicrotask(() => maybeDispatchCombinedListingPreselSync(el));
  }
  syncExplicitSwatchIfStackPreselected();
}

document.addEventListener('keydown', onStackRadioKeydown, { capture: true });
document.addEventListener('click', onStackFormOptionActivate, { capture: true });
document.addEventListener('change', onChange, { capture: true });
document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate, { capture: true });
document.addEventListener(CartAddEvent.eventName, (e) => {
  if (!(e instanceof CartAddEvent)) return;
  const card = e.target instanceof Element ? e.target.closest('product-card') : null;
  if (!(card instanceof Element)) return;
  if (!card.querySelector('.options-block--opt-stack')) return;
  if (e.detail?.data?.didError) {
    handleStackCartAddFailure(card);
    return;
  }
  resetStackAfterCartAdd(card);
});

document.addEventListener(ThemeEvents.cartError, (e) => {
  if (!(e instanceof CartErrorEvent)) return;
  const card = e.target instanceof Element ? e.target.closest('product-card') : null;
  if (!(card instanceof Element)) return;
  if (!card.querySelector('.options-block--opt-stack')) return;
  const idInput = card.querySelector('quick-add-component form input[name="id"]');
  const variantId = idInput instanceof HTMLInputElement ? idInput.value : '';
  handleStackCartAddFailure(card, variantId);
});

document.addEventListener('product-card:refresh-option-stacks', (e) => {
  if (!(e instanceof CustomEvent)) return;
  const card = e.detail?.card;
  if (!(card instanceof HTMLElement)) return;
  for (const el of card.querySelectorAll('.options-block--opt-stack')) {
    if (el instanceof HTMLElement) {
      clearQuickOnEnter(el);
      runRoot(el, false);
      queueMicrotask(() => maybeDispatchCombinedListingPreselSync(el));
    }
  }
  syncExplicitSwatchIfStackPreselected(card);
});
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
document.addEventListener('shopify:section:load', init);
